import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react"
import clsx from "clsx"

import { DatePicker } from "components/date/DatePicker"
import { PurpleToggle } from "components/toggles/PurpleToggle"

import { useTimezone } from "hooks/useTimezone"

import { getLanguageName } from "constants/languages"

import { maintenanceService } from "services/maintenance/maintenance.service"
import { MaintenanceSchedulePayload, MaintenanceSeverity, MaintenanceStatus } from "services/maintenance/type"

// Get user's timezone
const getUserTimezone = (): string => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const offset = new Date().getTimezoneOffset()
    const hours = Math.abs(Math.floor(offset / 60))
    const minutes = Math.abs(offset % 60)
    const sign = offset <= 0 ? "+" : "-"
    const offsetStr = `UTC${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
    return `${timezone} (${offsetStr})`
  } catch {
    return "Local Time"
  }
}

const SEVERITY_OPTIONS: { value: MaintenanceSeverity; label: string; color: string }[] = [
  { value: "info", label: "Info (Blue)", color: "#3b82f6" },
  { value: "warning", label: "Warning (Yellow)", color: "#eab308" },
  { value: "critical", label: "Critical (Red)", color: "#ef4444" }
]

type Props = {
  enabledLanguages: Record<string, boolean>
}

interface LocalizationEntry {
  lang: string
  title: string
  message: string
}

const getDefaultFormState = (): {
  enabled: boolean
  startTime: Date | null
  endTime: Date | null
  severity: MaintenanceSeverity
  showCountdown: boolean
  dismissible: boolean
  localizations: LocalizationEntry[]
} => ({
  enabled: true,
  startTime: null,
  endTime: null,
  severity: "warning",
  showCountdown: true,
  dismissible: true,
  localizations: [{ lang: "en", title: "", message: "" }]
})

export const MaintenanceSettingsTab: FC<Props> = ({ enabledLanguages }): ReactElement => {
  const { formatDateTime } = useTimezone()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState<MaintenanceStatus | null>(null)
  const [showSeverityDropdown, setShowSeverityDropdown] = useState(false)
  const [form, setForm] = useState(getDefaultFormState())
  const [initialForm, setInitialForm] = useState(getDefaultFormState())
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)

  const userTimezone = useMemo(() => getUserTimezone(), [])

  // Check if form has changes compared to initial state
  const hasChanges = useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initialForm)
  }, [form, initialForm])

  // Check if there's any schedule data (either on server or in form)
  const hasScheduleData = useMemo(() => {
    const englishLoc = form.localizations.find((l) => l.lang === "en")
    return !!(form.startTime || form.endTime || englishLoc?.title?.trim() || englishLoc?.message?.trim() || status?.schedule)
  }, [form, status])

  // Get list of enabled languages (excluding English which is always present)
  const availableLanguages = useMemo(() => {
    const enabled = Object.entries(enabledLanguages)
      .filter(([code, isEnabled]) => isEnabled && code !== "en")
      .map(([code]) => code)
    return enabled
  }, [enabledLanguages])

  // Languages that can still be added (not yet in form)
  const addableLanguages = useMemo(() => {
    const existingLangs = form.localizations.map((l) => l.lang)
    return availableLanguages.filter((lang) => !existingLangs.includes(lang))
  }, [availableLanguages, form.localizations])

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    try {
      const data = await maintenanceService.getStatus()
      setStatus(data)

      // If there's an existing schedule, populate the form
      if (data?.schedule) {
        const schedule = data.schedule
        const localizations: LocalizationEntry[] = [
          {
            lang: "en",
            title: schedule.title?.en || "",
            message: schedule.message?.en || ""
          }
        ]

        // Add other languages that have content
        Object.keys(schedule.title || {}).forEach((lang) => {
          if (lang !== "en" && (schedule.title?.[lang] || schedule.message?.[lang])) {
            localizations.push({
              lang,
              title: schedule.title?.[lang] || "",
              message: schedule.message?.[lang] || ""
            })
          }
        })

        const formData = {
          enabled: schedule.enabled,
          startTime: schedule.startTime ? new Date(schedule.startTime) : null,
          endTime: schedule.endTime ? new Date(schedule.endTime) : null,
          severity: schedule.severity,
          showCountdown: schedule.showCountdown,
          dismissible: schedule.dismissible,
          localizations
        }

        setForm(formData)
        setInitialForm(formData)
      } else {
        const defaultForm = getDefaultFormState()
        setForm(defaultForm)
        setInitialForm(defaultForm)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleToggleEnabled = async (activate: boolean) => {
    if (activate) {
      // Check if we have required data to activate
      const englishLoc = form.localizations.find((l) => l.lang === "en")
      if (!form.startTime || !englishLoc?.title?.trim() || !englishLoc?.message?.trim()) {
        alert("Please fill in Start Time, Title and Message before activating")
        return
      }
    }

    // Build title and message objects from form or existing schedule
    const title: Record<string, string> = {}
    const message: Record<string, string> = {}

    if (activate) {
      form.localizations.forEach((loc) => {
        if (loc.title.trim()) title[loc.lang] = loc.title.trim()
        if (loc.message.trim()) message[loc.lang] = loc.message.trim()
      })
    } else if (status?.schedule) {
      Object.assign(title, status.schedule.title)
      Object.assign(message, status.schedule.message)
    }

    const payload: MaintenanceSchedulePayload = {
      enabled: activate,
      startTime: form.startTime?.toISOString() || status?.schedule?.startTime || new Date().toISOString(),
      endTime: form.endTime?.toISOString() || status?.schedule?.endTime || null,
      title,
      message,
      severity: form.severity || status?.schedule?.severity || "warning",
      showCountdown: form.showCountdown ?? status?.schedule?.showCountdown ?? true,
      dismissible: form.dismissible ?? status?.schedule?.dismissible ?? true
    }

    const result = await maintenanceService.updateSchedule(payload)
    if (result) {
      const newForm = { ...form, enabled: activate }
      setForm(newForm)
      setInitialForm(newForm)
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              schedule: { ...result, enabled: activate }
            }
          : prev
      )
    }
  }

  const handleClearForm = () => {
    if (!confirm("Clear all form data? This will not affect the saved schedule on server.")) return
    const defaultForm = getDefaultFormState()
    setForm(defaultForm)
  }

  const handleResetForm = () => {
    setForm(initialForm)
  }

  const handleDeleteSchedule = async () => {
    if (!confirm("Delete schedule permanently? This will remove all schedule data from the server.")) return
    const success = await maintenanceService.deleteSchedule()
    if (success) {
      const defaultForm = getDefaultFormState()
      setForm(defaultForm)
      setInitialForm(defaultForm)
      setStatus((prev) => (prev ? { ...prev, schedule: null } : prev))
    }
  }

  const handleAddLanguage = (lang: string) => {
    setForm((prev) => ({
      ...prev,
      localizations: [...prev.localizations, { lang, title: "", message: "" }]
    }))
    setShowLanguageDropdown(false)
  }

  const handleRemoveLanguage = (lang: string) => {
    if (lang === "en") return // Cannot remove English
    setForm((prev) => ({
      ...prev,
      localizations: prev.localizations.filter((l) => l.lang !== lang)
    }))
  }

  const handleLocalizationChange = (lang: string, field: "title" | "message", value: string) => {
    setForm((prev) => ({
      ...prev,
      localizations: prev.localizations.map((l) => (l.lang === lang ? { ...l, [field]: value } : l))
    }))
  }

  const handleSubmit = async () => {
    // Validation
    if (!form.startTime) {
      alert("Start time is required")
      return
    }

    const englishLoc = form.localizations.find((l) => l.lang === "en")
    if (!englishLoc?.title?.trim()) {
      alert("English title is required")
      return
    }
    if (!englishLoc?.message?.trim()) {
      alert("English message is required")
      return
    }

    if (form.endTime && form.endTime <= form.startTime) {
      alert("End time must be after start time")
      return
    }

    setSaving(true)
    try {
      // Build title and message objects
      const title: Record<string, string> = {}
      const message: Record<string, string> = {}

      form.localizations.forEach((loc) => {
        if (loc.title.trim()) title[loc.lang] = loc.title.trim()
        if (loc.message.trim()) message[loc.lang] = loc.message.trim()
      })

      const payload: MaintenanceSchedulePayload = {
        enabled: form.enabled,
        startTime: form.startTime.toISOString(),
        endTime: form.endTime ? form.endTime.toISOString() : null,
        title,
        message,
        severity: form.severity,
        showCountdown: form.showCountdown,
        dismissible: form.dismissible
      }

      const result = await maintenanceService.updateSchedule(payload)
      if (result) {
        fetchStatus()
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <section className="flex flex-col gap-6">
        <h3 className="text-xl font-semibold mb-2">Maintenance</h3>
        <p>Loading...</p>
      </section>
    )
  }

  return (
    <section className="flex flex-col gap-6">
      <h3 className="text-xl font-semibold mb-2">Maintenance</h3>

      {/* Status Card */}
      <div className="p-4 rounded-lg border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-base font-medium text-gray-800 dark:text-white">Schedule Status:</span>
            <span
              className={clsx("px-3 py-1 rounded-full text-sm font-semibold", {
                "bg-[rgba(132,_0,_168,_1)] text-white": status?.schedule?.enabled,
                "bg-gray-500 text-white": !status?.schedule?.enabled && status?.schedule,
                "bg-gray-400 text-white": !status?.schedule
              })}
            >
              {status?.schedule?.enabled ? "ACTIVE" : status?.schedule ? "INACTIVE (Draft)" : "NO SCHEDULE"}
            </span>
          </div>

          {/* Toggle Active */}
          <PurpleToggle
            checked={status?.schedule?.enabled ?? false}
            disabled={!hasScheduleData}
            onChange={() => handleToggleEnabled(!status?.schedule?.enabled)}
            label="Message is Active"
          />
        </div>

        {/* Scheduled info */}
        {status?.schedule && status.schedule.startTime && (
          <div className="mt-4 pt-4 border-t border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)]">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">Scheduled:</span> {formatDateTime(status.schedule.startTime)}
              {status.schedule.endTime && <span> — {formatDateTime(status.schedule.endTime)}</span>}
              {!status.schedule.enabled && <span className="ml-2 text-yellow-600">(Draft - not visible to users)</span>}
            </p>
          </div>
        )}
      </div>

      {/* Schedule Form */}
      <div className="p-6 rounded-lg border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A]">
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Schedule Maintenance</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Start Time */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Start Time <span className="text-red-500">*</span>
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">({userTimezone})</span>
            </label>
            <DatePicker
              selected={form.startTime}
              onChange={(date: Date | null) => setForm((prev) => ({ ...prev, startTime: date }))}
              showTimeInput
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              minDate={new Date()}
              placeholderText="Select start date and time"
              className="w-full px-3 py-2 border rounded-lg bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.1)] text-gray-800 dark:text-white"
            />
          </div>

          {/* End Time */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              End Time (Optional)
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">({userTimezone})</span>
            </label>
            <DatePicker
              selected={form.endTime}
              onChange={(date: Date | null) => setForm((prev) => ({ ...prev, endTime: date }))}
              showTimeInput
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy HH:mm"
              minDate={form.startTime || new Date()}
              placeholderText="Select end date and time"
              className="w-full px-3 py-2 border rounded-lg bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.1)] text-gray-800 dark:text-white"
            />
          </div>

          {/* Severity */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Severity</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowSeverityDropdown(!showSeverityDropdown)}
                className="w-full px-3 py-2 border rounded-lg bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.1)] text-left flex items-center justify-between"
              >
                <span style={{ color: SEVERITY_OPTIONS.find((opt) => opt.value === form.severity)?.color }}>
                  {SEVERITY_OPTIONS.find((opt) => opt.value === form.severity)?.label}
                </span>
                <svg
                  className={clsx("w-4 h-4 text-gray-500 transition-transform", { "rotate-180": showSeverityDropdown })}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showSeverityDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#0C0C1A] border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg shadow-lg z-10">
                  {SEVERITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, severity: opt.value }))
                        setShowSeverityDropdown(false)
                      }}
                      style={{ color: opt.color }}
                      className={clsx(
                        "w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-[rgba(255,255,255,0.05)] first:rounded-t-lg last:rounded-b-lg",
                        { "bg-gray-100 dark:bg-[rgba(255,255,255,0.05)]": form.severity === opt.value }
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="flex flex-col gap-4">
            <PurpleToggle
              checked={form.showCountdown}
              onChange={() => setForm((prev) => ({ ...prev, showCountdown: !prev.showCountdown }))}
              label="Show countdown timer"
            />

            <PurpleToggle
              checked={form.dismissible}
              onChange={() => setForm((prev) => ({ ...prev, dismissible: !prev.dismissible }))}
              label="User can dismiss banner"
            />
          </div>
        </div>

        {/* Localizations */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-base font-medium text-gray-800 dark:text-white">Messages</h5>

            {/* Add Language Button */}
            {addableLanguages.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  className="px-4 py-2 rounded-lg border border-[rgba(132,_0,_168,_0.8)] text-[rgba(132,_0,_168,_1)] dark:text-white text-sm font-medium hover:bg-[rgba(169,78,206,0.1)] transition-colors flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Add Language
                </button>

                {showLanguageDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#0C0C1A] border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] rounded-lg shadow-lg z-10">
                    {addableLanguages.map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => handleAddLanguage(lang)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-[rgba(255,255,255,0.05)] first:rounded-t-lg last:rounded-b-lg"
                      >
                        {getLanguageName(lang)} ({lang})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            {form.localizations.map((loc) => (
              <div
                key={loc.lang}
                className="p-4 rounded-lg border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] bg-[rgba(0,0,0,0.02)] dark:bg-[rgba(255,255,255,0.02)]"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-800 dark:text-white">
                    {getLanguageName(loc.lang)}
                    {loc.lang === "en" && <span className="ml-2 text-xs text-gray-500">(Required)</span>}
                  </span>
                  {loc.lang !== "en" && (
                    <button
                      type="button"
                      onClick={() => handleRemoveLanguage(loc.lang)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Title {loc.lang === "en" && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      value={loc.title}
                      onChange={(e) => handleLocalizationChange(loc.lang, "title", e.target.value)}
                      placeholder="e.g., Scheduled Maintenance"
                      className="w-full px-3 py-2 border rounded-lg bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.1)] text-gray-800 dark:text-white text-sm"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Message {loc.lang === "en" && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={loc.message}
                      onChange={(e) => handleLocalizationChange(loc.lang, "message", e.target.value)}
                      placeholder="e.g., We will be performing system updates. Expected duration: 2 hours."
                      rows={3}
                      className="w-full px-3 py-2 border rounded-lg bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.1)] text-gray-800 dark:text-white text-sm resize-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-6 flex justify-between items-center">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClearForm}
              className="px-4 py-2 rounded-lg border border-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.2)] text-gray-800 dark:text-white text-sm font-medium hover:bg-gray-100 dark:hover:bg-[rgba(255,255,255,0.05)] transition-colors"
            >
              Clear Form
            </button>
            {hasChanges && (
              <button
                type="button"
                onClick={handleResetForm}
                className="px-4 py-2 rounded-lg border border-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.2)] text-gray-600 dark:text-gray-400 text-sm font-medium hover:bg-gray-100 dark:hover:bg-[rgba(255,255,255,0.05)] transition-colors"
              >
                Reset Changes
              </button>
            )}
            {hasScheduleData && (
              <button
                type="button"
                onClick={handleDeleteSchedule}
                className="px-4 py-2 rounded-lg border border-red-500 text-red-500 text-sm font-medium hover:bg-red-500 hover:text-white transition-colors"
              >
                Delete Schedule
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !hasChanges}
            className="px-6 py-2 rounded-lg bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(132,_0,_168,_0.8)] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Schedule"}
          </button>
        </div>
      </div>
    </section>
  )
}
